import React from 'react'
import SellableCardLabel from '../Label'
import { Image } from '@deal/components'
import { formatSellablePricing, getSellableUrl } from '#src/services/sellable'
import { Link } from 'react-router-dom'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import { IdentityConsumer } from '#src/containers/Identity'
import DefaultImage from './default-image.jpg'
import SaveSellable from '#src/components/SaveSellable'
import { SellableCardFragment, MyselfFragment } from '#src/generated/graphql'
import { SellableCardProps } from '../index'
import omit from 'lodash/omit'
import classnames from 'classnames'
import styles from './styles.css'
import { getSellableEcommerceProperties } from '#src/services/tracking'
import { Experiment, Variation } from '@deal/experiment-js'

interface Props {
  preferDefaultVariant: boolean
}

class SellableVariantCard extends React.Component<SellableCardProps & WithAnalyticsProps & Props> {
  public constructor(props: SellableCardProps & WithAnalyticsProps & Props) {
    super(props)

    this.handleClick = this.handleClick.bind(this)
    this.getImageUrl = this.getImageUrl.bind(this)
    this.getSellableUrl = this.getSellableUrl.bind(this)
    this.getFormattedPricing = this.getFormattedPricing.bind(this)
  }

  public handleClick() {
    const { analytics, position, sellable } = this.props

    if (analytics) {
      analytics.events.productClicked({
        ...getSellableEcommerceProperties(sellable),
        position: position,
        url: this.getSellableUrl(),
        image_url: this.getImageUrl(sellable)
      })
    }
  }

  public getFormattedPricing() {
    return formatSellablePricing(
      this.props.sellable,
      {
        percentSavingsDisplayThreshold: 5
      },
      {
        preferDefaultVariant: this.props.preferDefaultVariant
      }
    )
  }

  public getImageUrl(sellable: SellableCardFragment) {
    if (this.props.preferDefaultVariant) {
      return sellable.variationMatrix &&
        sellable.variationMatrix.defaultVariant &&
        sellable.variationMatrix.defaultVariant.images.length > 0
        ? sellable.variationMatrix.defaultVariant.images[0].url
        : sellable.images.length > 0
        ? sellable.images[0].url
        : DefaultImage
    }

    return sellable.images.length > 0 ? sellable.images[0].url : DefaultImage
  }

  public getSellableUrl() {
    const { sellable } = this.props
    const targetSellable =
      this.props.preferDefaultVariant && sellable.variationMatrix
        ? sellable.variationMatrix.defaultVariant
        : sellable
    return getSellableUrl(targetSellable)
  }

  public renderContent(myself: MyselfFragment | null) {
    const { sellable, lazyLoad } = this.props
    const sellableImageUrl = this.getImageUrl(sellable)
    const formattedPricing = this.getFormattedPricing()
    return (
      <>
        <SellableCardLabel sellable={sellable} />
        <section className={styles.section}>
          {myself && (
            <SaveSellable
              sellable={sellable}
              iconClassName={styles.saveIcon}
              containerClassName={styles.saveContainer}
            />
          )}
          <div className={styles.imageContainer}>
            <Image
              lazyLoad={lazyLoad}
              src={sellableImageUrl ? sellableImageUrl : DefaultImage}
              sizes="(min-width: 64em) 25vw, (min-width: 52em) 33vw, (min-width: 40em) 50vw, 100vw"
              className={styles.image}
            />
          </div>
        </section>

        <section className={styles.details}>
          {sellable.brand && sellable.brand.logo && (
            <div className={styles.brandLogo}>
              <Image
                src={sellable.brand.logo.url}
                width={36}
                height={36}
                imgixParams={{ fit: 'clip' }}
              />
            </div>
          )}

          <div className={styles.brand}>{sellable.brand && sellable.brand.displayName}</div>

          <div className={styles.title} data-testid="favorite-title">
            {sellable.title}
          </div>

          {formattedPricing && (
            <div className={styles.pricing}>
              <div className={styles.cost}>
                <div className={styles.price}>
                  <div className={styles.realPrice}>{formattedPricing.price}</div>
                </div>
                {formattedPricing.msrp && (
                  <div className={styles.msrp}>was {formattedPricing.msrp}</div>
                )}
              </div>

              {formattedPricing.savingsPercentage && (
                <div className={styles.savings}>{formattedPricing.savingsPercentage} OFF</div>
              )}
            </div>
          )}
        </section>
      </>
    )
  }

  public render() {
    const { className, onClick, ...rest } = this.props
    return (
      <IdentityConsumer>
        {({ myself }) => (
          <div
            className={classnames(className, styles.card)}
            {...omit(rest, 'preferDefaultVariant', 'analytics', 'position')}
          >
            {onClick ? (
              <div
                onClick={e => {
                  onClick(e)
                  this.handleClick
                }}
              >
                {this.renderContent(myself)}
              </div>
            ) : (
              <Link to={this.getSellableUrl()} onClick={this.handleClick}>
                {this.renderContent(myself)}
              </Link>
            )}
          </div>
        )}
      </IdentityConsumer>
    )
  }
}

const SellableCard: React.SFC<SellableCardProps & WithAnalyticsProps> = props => {
  return (
    <Experiment id="sellable-variants-search-index" default="off">
      <Variation id="off">
        <SellableVariantCard preferDefaultVariant={true} {...props} />
      </Variation>
      <Variation id="on">
        <SellableVariantCard preferDefaultVariant={false} {...props} />
      </Variation>
    </Experiment>
  )
}

export default withAnalytics(SellableCard)
